import { useId, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../cn";

export interface Tab {
  id: string;
  label: ReactNode;
  content: ReactNode;
}

export interface TabsProps {
  tabs: Tab[];
  active: string;
  onChange: (id: string) => void;
  className?: string;
  /** Classes merged onto every tab panel, which defaults to `pt-4`. */
  panelClassName?: string;
  /**
   * `inset` draws every label in ink at the page's weight, the selected one
   * with a 2px accent rule inset 8px under it (a hairline on hover). Opt-in;
   * `default` is the full-width rule with the unselected labels quieter.
   */
  look?: "default" | "inset";
}

/**
 * A tab set with the ARIA wiring a screen reader needs: the tablist, the
 * selected state, and each panel pointing back at the tab that controls it.
 */
export function Tabs({
  tabs,
  active,
  onChange,
  className,
  panelClassName,
  look = "default",
}: TabsProps) {
  const base = useId();

  function onTabKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (tabs.length === 0) return;
    const index = Math.max(
      0,
      tabs.findIndex((tab) => tab.id === active),
    );
    let next: number;
    switch (event.key) {
      case "ArrowRight":
        next = (index + 1) % tabs.length;
        break;
      case "ArrowLeft":
        next = (index - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const nextTab = tabs[next];
    if (!nextTab) return;
    onChange(nextTab.id);
    const target =
      event.currentTarget.querySelectorAll<HTMLButtonElement>("[role='tab']")[next];
    target?.focus();
    target?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }

  return (
    <div className={className}>
      <div
        role="tablist"
        tabIndex={-1}
        onKeyDown={onTabKeyDown}
        className="flex max-w-full gap-1 overflow-x-auto border-b border-rule whitespace-nowrap"
      >
        {tabs.map((tab) => {
          const selected = tab.id === active;
          return (
            <button
              key={tab.id}
              role="tab"
              id={`${base}-tab-${tab.id}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              aria-controls={`${base}-panel-${tab.id}`}
              onClick={() => {
                onChange(tab.id);
              }}
              className={
                look === "inset"
                  ? cn(
                      "relative shrink-0 px-3 py-2.5 text-ui text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                      "after:absolute after:right-2 after:bottom-[-1px] after:left-2 after:h-0.5 after:rounded-sm",
                      selected ? "after:bg-accent" : "after:bg-transparent hover:after:bg-rule",
                    )
                  : cn(
                      "-mb-px shrink-0 border-b-2 px-3 py-2 font-sans text-ui transition-colors",
                      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                      selected
                        ? "border-accent font-medium text-ink"
                        : "border-transparent text-ink-3 hover:text-ink-2",
                    )
              }
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`${base}-panel-${tab.id}`}
          aria-labelledby={`${base}-tab-${tab.id}`}
          hidden={tab.id !== active}
          className={cn("pt-4", panelClassName)}
        >
          {tab.id === active ? tab.content : null}
        </div>
      ))}
    </div>
  );
}
